import React, { useEffect, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers';
import type { ReportApiDateRangeQuery } from '../../types/report';
import {
  getLast30DaysInclusiveRange,
  getLast365DaysInclusiveRange,
  getLast7DaysInclusiveRange,
  getTodayCalendarRange,
  toYyyyMmDd,
  type ReportDateRangeYmd,
} from '../../utils/reportDateRanges';

export type ReportDatePreset = 'daily' | 'weekly' | 'monthly' | 'annual';

export type ReportDateRangeFilterProps = {
  /** Called when at least one date is set; `null` when both are cleared. */
  onRangeChange: (range: ReportApiDateRangeQuery | null) => void;
  /** Starts with this period selected. The page must already hold the same range: nothing is emitted on mount. */
  initialPreset?: ReportDatePreset;
};

function ymdToLocalDate(ymd: string) {
  return new Date(`${ymd}T00:00:00`);
}

function rangeForShortcut(s: ReportDatePreset): ReportDateRangeYmd {
  switch (s) {
    case 'daily':
      return getTodayCalendarRange();
    case 'weekly':
      return getLast7DaysInclusiveRange();
    case 'monthly':
      return getLast30DaysInclusiveRange();
    case 'annual':
      return getLast365DaysInclusiveRange();
    default:
      return getTodayCalendarRange();
  }
}

/** Internal Select value when the user edited From/To manually (not in the menu). */
const CUSTOM_SELECT_VALUE = 'custom';

/** No period / dates chosen yet (no API filter). */
const PERIOD_EMPTY_VALUE = '';

/** Period menu: reset preset and both dates. */
const CLEAR_ALL_MENU_VALUE = '__clear_all__';

export function ReportDateRangeFilter({ onRangeChange, initialPreset }: ReportDateRangeFilterProps) {
  const initialRange = initialPreset ? rangeForShortcut(initialPreset) : null;
  const [startDate, setStartDate] = useState(initialRange?.startDate ?? '');
  const [endDate, setEndDate] = useState(initialRange?.endDate ?? '');
  /** Preset from Period, or `null` for empty/custom partial or full custom range. */
  const [shortcut, setShortcut] = useState<ReportDatePreset | null>(initialPreset ?? null);
  const lastEmittedKey = useRef(initialRange ? `${initialRange.startDate}|${initialRange.endDate}` : '');

  let selectValue: string;
  if (shortcut !== null) {
    selectValue = shortcut;
  } else if (!startDate && !endDate) {
    selectValue = PERIOD_EMPTY_VALUE;
  } else {
    selectValue = CUSTOM_SELECT_VALUE;
  }

  const handleShortcutChange = (event: SelectChangeEvent<string>) => {
    const v = event.target.value;
    if (v === CLEAR_ALL_MENU_VALUE) {
      setShortcut(null);
      setStartDate('');
      setEndDate('');
      return;
    }
    if (v === PERIOD_EMPTY_VALUE || v === CUSTOM_SELECT_VALUE) return;
    if (v === 'daily' || v === 'weekly' || v === 'monthly' || v === 'annual') {
      setShortcut(v);
      const r = rangeForShortcut(v);
      setStartDate(r.startDate);
      setEndDate(r.endDate);
    }
  };

  const handleStartPickerChange = (date: Date | null) => {
    setShortcut(null);
    setStartDate(date ? toYyyyMmDd(date) : '');
  };

  const handleEndPickerChange = (date: Date | null) => {
    setShortcut(null);
    setEndDate(date ? toYyyyMmDd(date) : '');
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      if (lastEmittedKey.current !== '') {
        lastEmittedKey.current = '';
        onRangeChange(null);
      }
      return;
    }
    const key = `${startDate}|${endDate}`;
    if (lastEmittedKey.current === key) return;
    lastEmittedKey.current = key;
    const range: ReportApiDateRangeQuery = {};
    if (startDate) range.startDate = startDate;
    if (endDate) range.endDate = endDate;
    onRangeChange(range);
  }, [startDate, endDate, onRangeChange]);

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        {/* Always shrink: value is often '' while renderValue still shows text, so MUI would otherwise overlap label + value. */}
        <InputLabel id="report-period-label" shrink>
          Period
        </InputLabel>
        <Select
          labelId="report-period-label"
          label="Period"
          notched
          displayEmpty
          value={selectValue}
          onChange={handleShortcutChange}
          renderValue={(value: string) => {
            if (value === PERIOD_EMPTY_VALUE) return 'Select period';
            if (value === CUSTOM_SELECT_VALUE) return 'Custom dates';
            if (value === 'daily') return 'Daily';
            if (value === 'weekly') return 'Weekly';
            if (value === 'monthly') return 'Monthly';
            if (value === 'annual') return 'Annual';
            return String(value);
          }}
        >
          <MenuItem value={PERIOD_EMPTY_VALUE} sx={{ display: 'none' }} aria-hidden />
          <MenuItem value={CLEAR_ALL_MENU_VALUE}>Clear filters</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="annual">Annual</MenuItem>
          <MenuItem value={CUSTOM_SELECT_VALUE} sx={{ display: 'none' }} aria-hidden>
            Custom
          </MenuItem>
        </Select>
      </FormControl>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <DatePicker
          label="From"
          value={startDate ? ymdToLocalDate(startDate) : null}
          onChange={handleStartPickerChange}
          slotProps={{
            textField: {
              size: 'small',
              inputProps: { 'aria-label': 'Start date' },
            },
          }}
        />
        {startDate ? (
          <IconButton size="small" aria-label="Clear from date" onClick={() => handleStartPickerChange(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <DatePicker
          label="To"
          value={endDate ? ymdToLocalDate(endDate) : null}
          onChange={handleEndPickerChange}
          minDate={startDate ? ymdToLocalDate(startDate) : undefined}
          slotProps={{
            textField: {
              size: 'small',
              inputProps: { 'aria-label': 'End date' },
            },
          }}
        />
        {endDate ? (
          <IconButton size="small" aria-label="Clear to date" onClick={() => handleEndPickerChange(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}
